<script>
	import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
	import { environment } from "@app/store/envStore.svelte.js";
	import { projectState } from "@app/store/projectStore.svelte.js";
    import { editorState } from "@app/store/editorStore.svelte.js";
    import {styleState} from "@app/store/styleStore.svelte.js";
	import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
	import { authState } from "@app/store/authStore.svelte.js";
	import { toast } from "@zerodevx/svelte-toast";
	import { speakerState } from "@app/store/speakerStore.svelte.js";
	import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
	import { onDestroy } from "svelte"; 
	import { fade } from "svelte/transition";
	import Papa from "papaparse";
	import Swal from "sweetalert2";
	import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
	import { BarLoader, Circle } from "svelte-loading-spinners";
	import * as m from '@app/paraglide/messages.js';

	/* CC LIB */
	import _Event from "@cclib/classes/event.js";
	import _EventGroup from "@cclib/classes/eventGroup.js";
	import _Speaker from "@cclib/classes/speaker.js";
	import decode from "@cclib/functions/decode.js";
	import defaults from "@cclib/lib/defaults.js";
	import encode from "@cclib/functions/encode.js";
	import formats from "@cclib/lib/formats.js";
	import alignCaptionFile from "@cclib/functions/special/alignCaptionFile.js";
	import convertToHtml from "@cclib/functions/quill/convertToHtml.js";
    import encodeHtml from "@cclib/functions/eventGroups/encodeHtml.js";
	import replaceTrialText from "@cclib/functions/eventGroups/replaceTrialText.js";
	import fixOverlap from '@cclib/functions/eventGroups/fixOverlap.js';
	import {
		promptAdTemplateMinGap,
		buildAdEventGroupTemplate,
		fillAdTemplateFromGaps,
	} from "@app/lib/adTemplate.js";
	/* Firebase */
	import firebase from "@app/configs/firebase.js";
	import "firebase/compat/functions";
	import db from "@app/configs/firestore.js";
	import storage from "@app/configs/storage.js";

	import { saveAs } from "file-saver";

	const PAGE_SIZE = 100;

	let jobs = $state([]),
		selectedJobIds = $state([]),
		lastSelectedIndex = $state(null),
		currentPage = $state(1),
		batchProcessing = $state(false),
		batchProgress = $state(0),
		batchTotal = $state(0),
		batchStatusMsg = $state(""),
		userId = firebase.auth().currentUser.uid,
		email = firebase.auth().currentUser.email,
		teamId = authState.team ? authState.team.id : null,
		dbRef = authState.team
			? db.collection("teams").doc(teamId).collection("jobs")
			: db.collection("users").doc(userId).collection("jobs"),
		storageRef = storage.ref(),
		storagePath = authState.team
			? `teams/${teamId}/transcripts/`
			: `users/${userId}/transcripts/`,
		deletingFile = $state(false),
		loading = $state(false),
		showOptions = $state(false),
		showDownloadOptions = $state(false),
		showBatchDownloadOptions = $state(false),
		autoRefresh = $state(true),
		dashboardDefaults = JSON.parse(localStorage.getItem("cc-transcription-dashboard-defaults")) || {},
		adDefaults =
			JSON.parse(localStorage.getItem("cc-ad-defaults-new")) || {},
		eventGroupDefaults =
			JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};

	let maxChars = eventGroupDefaults.maxChars || 32;
	let maxLines = eventGroupDefaults.maxLines || 2;

	let dateRange = $state(dashboardDefaults.dateRange || "week"),
		startDate = $state(dashboardDefaults.startDate),
		endDate = $state(dashboardDefaults.endDate),
		statusFilter = $state("All"),
		pending = false,
		gettingJobs = $state(getTranscriptJobs());

	let totalPages = $derived(Math.max(1, Math.ceil(jobs.length / PAGE_SIZE)));
	let pagedJobs = $derived(
		jobs.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
	);
	let selectedJob = $derived(
		selectedJobIds.length === 1
			? jobs.find((job) => job.id === selectedJobIds[0])
			: undefined,
	);

	function toggleJobSelection(jobId) {
		if (selectedJobIds.includes(jobId)) {
			selectedJobIds = selectedJobIds.filter((id) => id !== jobId);
		} else {
			selectedJobIds = [...selectedJobIds, jobId];
		}
	}

	function handleRowClick(event, job, index) {
		if (event.shiftKey && lastSelectedIndex !== null) {
			let start = Math.min(lastSelectedIndex, index);
			let end = Math.max(lastSelectedIndex, index);
			selectedJobIds = pagedJobs
				.slice(start, end + 1)
				.map((j) => j.id);
		} else if (event.ctrlKey || event.metaKey) {
			toggleJobSelection(job.id);
			lastSelectedIndex = index;
		} else {
			selectedJobIds = [job.id];
			lastSelectedIndex = index;
		}
	}

	function selectAllOnPage() {
		let merged = new Set([
			...selectedJobIds,
			...pagedJobs.map((job) => job.id),
		]);
		selectedJobIds = [...merged];
	}

	function clearSelection() {
		selectedJobIds = [];
		lastSelectedIndex = null;
	}

	let autoRefreshInterval = setInterval(function () {
		if (!loading && !pending) {
			gettingJobs = getTranscriptJobs();
		}
	}, 10000);

	onDestroy(() => {
		if (autoRefreshInterval) {
			clearInterval(autoRefreshInterval);
		}
	});

	function disableAutoRefresh() {
		clearInterval(autoRefreshInterval);
		autoRefreshInterval = undefined;
		autoRefresh = false;
	}

	function enableAutoRefresh() {
		if (autoRefreshInterval) {
			clearInterval(autoRefreshInterval);
		}

		autoRefreshInterval = setInterval(function () {
			if (!loading && !pending) {
				gettingJobs = getTranscriptJobs();
			}
		}, 10000);

		autoRefresh = true;
	}

	function getDateRange() {
		let start, end;
		switch (dateRange) {
			case "custom":
				start = new Date(startDate);
				end = new Date(endDate);
				break;
			case "week":
				// code block
				start = new Date();
				end = new Date();
				start.setHours(start.getHours() - 168);
				break;
			case "month":
				// code block
				start = new Date();
				end = new Date();
				start.setMonth(start.getMonth() - 1);
				break;
			case "year":
				// code block
				start = new Date();
				end = new Date();
				start.setFullYear(start.getFullYear() - 1);
				break;
			default:
				start = new Date();
				end = new Date();
				start.setHours(start.getHours() - 24);
		}

		return {
			start: start,
			end: end,
		};
	}

	function getTranscriptJobs() {
		if (pending === true) {
			return;
		}
		pending = true;
		let queryDateRange = getDateRange();
		//console.log(queryDateRange);

		selectedJobIds = [];
		lastSelectedIndex = null;
		currentPage = 1;
		jobs = [];

		return dbRef
			.where("createdOn", ">=", queryDateRange.start)
			.where("createdOn", "<=", queryDateRange.end)
			.where("type", "==", "transcription")
			.where(
				"status",
				"in",
				statusFilter === "All"
					? [
							"Submitted",
							"Passed",
							"Failed",
							"Submitted",
							"In Progress",
						]
					: [statusFilter],
			)
			.orderBy("createdOn", "desc")
			.get()
			.then((querySnapshot) => {
				querySnapshot.forEach((doc) => {
					//console.log(doc.data());
					if (!doc.data().archived) {
						jobs = [...jobs, doc.data()];
					}
				});

				if (allJobsComplete(jobs)) {
					disableAutoRefresh();
				}
			})
			.catch((error) => {
				console.log("Error getting documents: ", error);
				toast.push(m.error_getting_transcripts(), {
					classes: ["toast-danger"],
				});
			})
			.finally(() => {
				pending = false;
			});
	}

	function allJobsComplete(jobs) {
		let inProgressJob = jobs.findIndex((job) => {
			return job.progress !== 100;
		});

		if (inProgressJob > -1) {
			return false;
		} else {
			return true;
		}
	}

	async function getTranscriptFile(job = selectedJob) {
		let url = await storageRef
			.child(storagePath + job.id + ".json")
			.getDownloadURL();
		let res = await fetch(url);
		let resJson = await res.json();
		return resJson;
	}

	async function importTranscript(type) {
		try {
			disableAutoRefresh();
			loading = true;

			toast.push(m.loading_transcript_please_wait(), {
				classes: ["toast-info"],
			});

			projectState.selected = false;

			await importJobTranscript(selectedJob, type);

			insertHistoryAction({
				name: "import transcript", //action name
				eventGroup: projectState.selected,
				snapshots: [
					{
						store: "eventGroupState",
						value: JSON.stringify(eventGroupState),
					},
				],
			});

			toast.push(m.transcript_import_success(), {
				classes: ["toast-success"],
			});
			/* Save default transcript dashboard settings to local storage */
			localStorage.setItem("cc-transcription-dashboard-defaults", JSON.stringify({
				dateRange : dateRange,
				startDate : startDate,
				endDate  : endDate		
			}));
		} catch (err) {
			console.error(err);
			console.error(err.message);

			toast.push(m.error_message({ error: err.message }), { classes: ["toast-danger"] });
		} finally {
			hideModal();
		}
	}

	async function importJobTranscript(job, type) {
			let resJson = await getTranscriptFile(job);
			let srcProfile = job.config.provider;			
			let options, transcriptEventGroup; 

			if (srcProfile === "Speechmatics" || srcProfile === "ElevenLabs Scribe"){
				options = new defaults.options({
					profile: formats.profileMapping[srcProfile].name,
					formatOptions: [
						{
							name: "Import Type",
							type: "list",
							values: ["subtitle", "transcription"],
							selected: "subtitle",
						},
						{
							name: "Max Characters",
							type: "number-input",
							values: "",
							selected: maxChars,
						},
						{
							name: "Max Lines",
							type: "number-input",
							values: "",
							selected: maxLines,
						},
					],
					frameRate: projectState.frameRate,
					dropFrame: projectState.dropFrame					
				});

				transcriptEventGroup = {...(await decode(
					JSON.stringify(resJson),
					options,
				))};

                transcriptEventGroup = encodeHtml(transcriptEventGroup);
                transcriptEventGroup = fixOverlap(transcriptEventGroup, editorState.minFrameGap, projectState.frameRate);
                				
				transcriptEventGroup.type = "subtitle";
				transcriptEventGroup.name = m.ai_event_group_name({ type, index: eventGroupState.length + 1 });
				transcriptEventGroup.maxCps = eventGroupDefaults.maxCps || 9999;
				transcriptEventGroup.maxWpm = eventGroupDefaults.maxWpm || 9999;
				transcriptEventGroup.maxChars = eventGroupDefaults.maxChars || 9999;
				transcriptEventGroup.maxLines = eventGroupDefaults.maxLines || 9999;
                transcriptEventGroup.minDuration = eventGroupDefaults.minDuration || 0.5;
                transcriptEventGroup.maxDuration = eventGroupDefaults.maxDuration || 12;
				transcriptEventGroup.overlap = eventGroupDefaults.overlap;
				transcriptEventGroup.illegalChars = eventGroupDefaults.illegalChars;
                transcriptEventGroup.fontFamily = eventGroupDefaults.fontFamily;
                transcriptEventGroup.fontSize = eventGroupDefaults.fontSize || 16;
				eventGroupState.push({...transcriptEventGroup});
				projectState.selected = eventGroupState.length - 1;
                transcriptEventGroup.speakers.forEach(sp =>{
                    speakerState.push(sp);
                })

				//Check that translations exist in the Speechmatics JSON and that the translations object has language properties
				if (resJson.translations && Object.keys(resJson.translations).length > 0){
					for (let i = 0; i<Object.keys(resJson.translations).length; i++){
						let lang = Object.keys(resJson.translations)[i];
						
						options = new defaults.options({
							profile: formats.profileMapping[srcProfile].name,
							formatOptions: [
								{
									name: "Import Type",
									type: "list",
									values: ["subtitle", "transcription"],
									selected: "subtitle",
								},
								{
									name: "Max Characters",
									type: "number-input",
									values: "",
									selected: maxChars,
								},
								{
									name: "Max Lines",
									type: "number-input",
									values: "",
									selected: maxLines,
								},
								{
									name: "Import Target",
									type: "list",
									values: ["Translation", "Transcript", "Audio Events"],
									selected: "Translation",
								},
								{
									name: "Language Code",
									type: "text-input",
									values: "",
									selected: lang,
								}
							],
							frameRate: projectState.frameRate,
							dropFrame: projectState.dropFrame
						});

						if (resJson.translations[lang].length > 0){
							transcriptEventGroup = {...(await decode(
								JSON.stringify(resJson),
								options,
							))};

                            transcriptEventGroup = encodeHtml(transcriptEventGroup);
                            transcriptEventGroup = fixOverlap(transcriptEventGroup, editorState.minFrameGap, projectState.frameRate);

							transcriptEventGroup.type = "subtitle";
							transcriptEventGroup.name = m.machine_translation_name({ language: lang });
							transcriptEventGroup.maxCps = eventGroupDefaults.maxCps || 9999;
							transcriptEventGroup.maxWpm = eventGroupDefaults.maxWpm || 9999;
							transcriptEventGroup.maxChars = eventGroupDefaults.maxChars || 9999;
							transcriptEventGroup.maxLines = eventGroupDefaults.maxLines || 9999;
                            transcriptEventGroup.minDuration = eventGroupDefaults.minDuration || 0.5;
                            transcriptEventGroup.maxDuration = eventGroupDefaults.maxDuration || 12;
							transcriptEventGroup.overlap = eventGroupDefaults.overlap;
							transcriptEventGroup.illegalChars = eventGroupDefaults.illegalChars;
							eventGroupState.push(transcriptEventGroup);
                            transcriptEventGroup.speakers.forEach(sp =>{
                                speakerState.push(sp);
                            });
						}
					};
				}

                if (srcProfile === "ElevenLabs Scribe"){
                    //Map the words where the type is set to `audio_event` to a new array called `audio_events`
					let wordsArray = resJson.words || resJson?.transcription?.words || [];
                    resJson.audio_events = wordsArray.filter((word) => {
                        return word.type === "audio_event";
                    });

                    if (resJson.audio_events.length === 0){
                        resJson.audio_events = null;
                    }
                }

				if (resJson.audio_events){
					options = new defaults.options({
						profile: formats.profileMapping[srcProfile].name,
						formatOptions: [
							{
								name: "Import Type",
								type: "list",
								values: ["subtitle", "transcription"],
								selected: "subtitle",
							},
							{
								name: "Max Characters",
								type: "number-input",
								values: "",
								selected: maxChars,
							},
							{
								name: "Max Lines",
								type: "number-input",
								values: "",
								selected: maxLines,
							},
							{
								name: "Import Target",
								type: "list",
								values: ["Translation", "Transcript", "Audio Events"],
								selected: "Audio Events",
							}
						],
						frameRate: projectState.frameRate,
						dropFrame: projectState.dropFrame
					});

					transcriptEventGroup = {...(await decode(
						JSON.stringify(resJson),
						options,
					))};

                    transcriptEventGroup = encodeHtml(transcriptEventGroup);
                    transcriptEventGroup = fixOverlap(transcriptEventGroup, editorState.minFrameGap, projectState.frameRate);

					transcriptEventGroup.type = "subtitle";
					transcriptEventGroup.name = m.audio_events_name();
					transcriptEventGroup.maxCps = eventGroupDefaults.maxCps || 9999;
					transcriptEventGroup.maxWpm = eventGroupDefaults.maxWpm || 9999;
					transcriptEventGroup.maxChars = eventGroupDefaults.maxChars || 9999;
					transcriptEventGroup.maxLines = eventGroupDefaults.maxLines || 9999;
                    transcriptEventGroup.minDuration = eventGroupDefaults.minDuration || 0.5;
                    transcriptEventGroup.maxDuration = eventGroupDefaults.maxDuration || 12;
					transcriptEventGroup.overlap = eventGroupDefaults.overlap;
					transcriptEventGroup.illegalChars = eventGroupDefaults.illegalChars;
                    eventGroupState.push(transcriptEventGroup);
				}
			} else {
				options = new defaults.options({
					profile: formats.profileMapping[srcProfile].name,
					formatOptions: [
						{
							name: "Import Type",
							type: "list",
							values: ["subtitle", "transcription"],
							selected: "subtitle",
						},
						{
							name: "Max Characters",
							type: "number-input",
							values: "",
							selected: maxChars,
						},
						{
							name: "Max Lines",
							type: "number-input",
							values: "",
							selected: maxLines,
						},
					],
					frameRate: projectState.frameRate,
					dropFrame: projectState.dropFrame
				});

				transcriptEventGroup = {...(await decode(
					JSON.stringify(resJson),
					options,
				))};

                transcriptEventGroup = encodeHtml(transcriptEventGroup);
                transcriptEventGroup = fixOverlap(transcriptEventGroup, editorState.minFrameGap, projectState.frameRate);

				transcriptEventGroup.type = "subtitle";
				transcriptEventGroup.name = m.ai_event_group_name({ type, index: eventGroupState.length + 1 });
				transcriptEventGroup.maxCps = eventGroupDefaults.maxCps || 9999;
				transcriptEventGroup.maxWpm = eventGroupDefaults.maxWpm || 9999;
				transcriptEventGroup.maxChars = eventGroupDefaults.maxChars || 9999;
				transcriptEventGroup.maxLines = eventGroupDefaults.maxLines || 9999;
                transcriptEventGroup.minDuration = eventGroupDefaults.minDuration || 0.5;
                transcriptEventGroup.maxDuration = eventGroupDefaults.maxDuration || 12;
				transcriptEventGroup.overlap = eventGroupDefaults.overlap;
				transcriptEventGroup.illegalChars = eventGroupDefaults.illegalChars;
				eventGroupState.push({...transcriptEventGroup});
				projectState.selected = eventGroupState.length - 1;
                transcriptEventGroup.speakers.forEach(sp =>{
                    speakerState.push(sp);
                });
			}
	}

	async function downloadSubtitle(profile) {
		try {
			console.log("Downloading subtitle file");
			disableAutoRefresh();
			loading = true;

			if (!profile) {
				/* if the profile is not found we need to use sweet alert to ask the user to select a profile from a list of profiles (e.g. Subtitles, or transcript) */
				let trgProfiles = formats.getTrgProfiles();
				let inputList = {};
				trgProfiles.forEach((profile) => {
					inputList[profile] = formats.getProfileTitleByName(profile);
				});

				let swalRes = await Swal.fire({
					...SWAL_BASE,
					customClass: SWAL_CLASSES.confirmCancel,
					title: m.select_profile_title(),
					text: m.select_profile_text(),
					input: "select",
					inputOptions: inputList,
					showCancelButton: true,
					confirmButtonText: m.download(),
					cancelButtonText: m.cancel(),
				});

				if (swalRes.isConfirmed) {
					profile = swalRes.value;
				} else {
					return;
				}
			}

			toast.push(m.downloading_subtitle_file_wait(), {
				classes: ["toast-info"],
			});

			let { fileBlob, fileName } = await buildJobDownload(selectedJob, profile);

			saveAs(fileBlob, fileName, {
				autoBom: true,
			});

			toast.push(m.subtitle_file_downloaded_success(), {
				classes: ["toast-success"],
			});
		} catch (err) {
			console.error(err);
			console.error(err.message);

			toast.push(m.error_message({ error: err.message }), { classes: ["toast-danger"] });
		} finally {
			hideModal();
		}
	}

	async function buildJobDownload(job, profile) {
			let resJson = await getTranscriptFile(job);
			if (profile === "raw") {
				let jsonBlob = new Blob([JSON.stringify(resJson)], {
					type: "text/plain;charset=utf-8",
				});

				return { fileBlob: jsonBlob, fileName: job.id + ".json" };
			}

			let transcriptEventGroup;
			let srcProfile = job.config.provider;
			let srcOptions = new defaults.options({
				profile: formats.profileMapping[srcProfile].name,
				formatOptions: [
					{
						name: "Import Type",
						type: "list",
						values: ["subtitle", "transcription"],
						selected: "subtitle",
					},
					{
						name: "Max Characters",
						type: "number-input",
						values: "",
						selected: maxChars,
					},
					{
						name: "Max Lines",
						type: "number-input",
						values: "",
						selected: maxLines,
					},
				],
				frameRate: projectState.frameRate,
				dropFrame: projectState.dropFrame
			});

			transcriptEventGroup = {...(await decode(
				JSON.stringify(resJson),
				srcOptions,
			))};

            transcriptEventGroup = encodeHtml(transcriptEventGroup);
            transcriptEventGroup = fixOverlap(transcriptEventGroup, editorState.minFrameGap, projectState.frameRate);

			if (authState.status === "in_trial") {
				transcriptEventGroup = replaceTrialText(transcriptEventGroup);
			}

			let trgOptions = new defaults.options({
				profile: profile,
				frameRate: projectState.frameRate,
				dropFrame: projectState.dropFrame
			});

			if (profile === "transcriptDocX") {
				profile = "dialogueListWord";
				trgOptions.profile = profile;
				trgOptions.encoding_options = [
					{
						name: "Export Title Page",
						type: "list",
						values: ["yes", "no"],
						selected: "no",
					},
					{
						name: "Title",
						type: "text-input",
						values: "",
						selected: "DEFAULT TITLE",
					},
					{
						name: "Export Metadata",
						type: "list",
						values: ["yes", "no"],
						selected: "no",
					},
					{
						name: "Export Event Id",
						type: "list",
						values: ["yes", "no"],
						selected: "yes",
					},
					{
						name: "Export Speakers",
						type: "list",
						values: ["yes", "no"],
						selected: "yes",
					},
					{
						name: "Export Tags",
						type: "list",
						values: ["yes", "no"],
						selected: "no",
					},
					{
						name: "Export Notes",
						type: "list",
						values: ["yes", "no"],
						selected: "no",
					},
					{
						name: "Export Reply",
						type: "list",
						values: ["yes", "no"],
						selected: "yes",
					},
					{
						name: "Timecode Format",
						type: "list",
						values: ["SMPTE", "Clock"],
						selected: "SMPTE",
					},
					{
						name: "Export Start Time",
						type: "list",
						values: ["yes", "no"],
						selected: "yes",
					},
					{
						name: "Export End Time",
						type: "list",
						values: ["yes", "no"],
						selected: "yes",
					},
					{
						name: "Export Duration",
						type: "list",
						values: ["yes", "no"],
						selected: "yes",
					},
				];
			} else if (profile === "transcriptTxt") {
				profile = "textTranscript";
				trgOptions.profile = profile;
				trgOptions.encoding_options = [
					{
						name: "Split On New Speaker",
						type: "list",
						values: ["yes", "no"],
						selected: "yes",
					},
					{
						name: "Split On Event Gap",
						type: "number-input",
						values: "",
						selected: 3,
					},
					{
						name: "Event Prefix",
						type: "text-input",
						values: "",
						selected: "",
					},
					{
						name: "Event Suffix",
						type: "text-input",
						values: "",
						selected: "",
					},
				];
			}

			let output = await encode(transcriptEventGroup, trgOptions);
			let defaultEncoding = formats.defaultEncodingMap[profile];
			let trgExt = formats.defaultExtMap[profile];
			let fileName = job.projectName + "." + trgExt;
			let fileBlob;

			if (defaultEncoding === "buffer") {
				if (trgExt === "xlsx") {
					fileBlob = new Blob([output], {
						type: "application/octet-stream",
					});
				} else {
					fileBlob = output;
				}
			} else if (defaultEncoding.toLowerCase() === "hex") {
				let byteArray = new Uint8Array(
					output.match(/.{2}/g).map((e) => parseInt(e, 16)),
				);
				fileBlob = new Blob([byteArray], {
					type: "application/octet-stream",
				});
			} else if (defaultEncoding.toLowerCase() === "utf-16le") {
				fileBlob = new Blob([output], {
					type: "text/plain;charset=utf-16le",
				});
			} else {
				fileBlob = new Blob([output], {
					type: "text/plain;charset=" + defaultEncoding,
				});
			}

			return { fileBlob, fileName };
	}

	async function batchImport(type) {
		let eligibleJobs = jobs.filter(
			(job) =>
				selectedJobIds.includes(job.id) && job.status === "Passed",
		);

		if (eligibleJobs.length === 0) {
			toast.push(m.batch_no_eligible_jobs(), {
				classes: ["toast-warning"],
			});
			return;
		}

		try {
			disableAutoRefresh();
			loading = true;
			batchProcessing = true;
			batchProgress = 0;
			batchTotal = eligibleJobs.length;
			projectState.selected = false;

			let completed = 0;
			for (let i = 0; i < eligibleJobs.length; i++) {
				let job = eligibleJobs[i];
				batchStatusMsg = m.batch_processing_job({ name: job.projectName });
				try {
					await importJobTranscript(job, type);
				} catch (err) {
					console.error(err, err.message);
					toast.push(m.error_message({ error: err.message }), {
						classes: ["toast-danger"],
					});
				}
				completed++;
				batchProgress = Math.round((completed / batchTotal) * 100);
			}

			insertHistoryAction({
				name: "import transcript", //action name
				eventGroup: projectState.selected,
				snapshots: [
					{
						store: "eventGroupState",
						value: JSON.stringify(eventGroupState),
					},
				],
			});

			localStorage.setItem("cc-transcription-dashboard-defaults", JSON.stringify({
				dateRange : dateRange,
				startDate : startDate,
				endDate  : endDate
			}));

			toast.push(m.batch_import_complete(), {
				classes: ["toast-success"],
			});
		} catch (err) {
			console.error(err, err.message);
			toast.push(m.error_message({ error: err.message }), {
				classes: ["toast-danger"],
			});
		} finally {
			batchProcessing = false;
			hideModal();
		}
	}

	async function batchDownload(profile) {
		let eligibleJobs = jobs.filter(
			(job) =>
				selectedJobIds.includes(job.id) && job.status === "Passed",
		);

		if (eligibleJobs.length === 0) {
			toast.push(m.batch_no_eligible_jobs(), {
				classes: ["toast-warning"],
			});
			return;
		}

		let targetFolder;
		if (environment.electron) {
			targetFolder = await selectTargetFolder();
			if (!targetFolder) {
				return;
			}
		}

		try {
			disableAutoRefresh();
			loading = true;
			batchProcessing = true;
			batchProgress = 0;
			batchTotal = eligibleJobs.length;

			let completed = 0;
			for (let i = 0; i < eligibleJobs.length; i++) {
				let job = eligibleJobs[i];
				batchStatusMsg = m.batch_processing_job({ name: job.projectName });
				try {
					let { fileBlob, fileName } = await buildJobDownload(job, profile);

					if (environment.electron) {
						let fs = window.fs;
						let path = window.path;
						let filePath = path.join(targetFolder, fileName);
						let fileArrayBuffer = await fileBlob.arrayBuffer();
						const fileBuffer = Buffer.from(fileArrayBuffer);
						await fs.writeFile(filePath, fileBuffer);
					} else {
						saveAs(fileBlob, fileName, {
							autoBom: true,
						});
					}
				} catch (err) {
					console.error(err, err.message);
					toast.push(m.error_message({ error: err.message }), {
						classes: ["toast-danger"],
					});
				}
				completed++;
				batchProgress = Math.round((completed / batchTotal) * 100);
			}

			toast.push(m.batch_download_complete(), {
				classes: ["toast-success"],
			});
		} catch (err) {
			console.error(err, err.message);
			toast.push(m.error_message({ error: err.message }), {
				classes: ["toast-danger"],
			});
		} finally {
			batchProcessing = false;
			hideModal();
		}
	}

	function selectTargetFolder() {
		return window.ipcRenderer
			.invoke("selectFolder", {
				title: m.batch_subtitle_export_select_folder_title(),
				properties: ["openDirectory", "createDirectory"],
			})
			.then((path) => {
				if (!path.canceled) {
					return path.filePaths[0];
				} else {
					return false;
				}
			});
	}

	async function automaticSync() {
		disableAutoRefresh();

		if (loading) {
			return;
		}

		toast.push(m.calculating_timecodes_wait(), {
			classes: ["toast-info"],
		});
		
		loading = true;

        //Use Sweet alert to ask the user if the Event Group has rough timecodes that need to be adjusted, or if it is a clean transcript that can be aligned directly.
		let captionFilePresentRes = await Swal.fire({
			...SWAL_BASE,
			customClass: SWAL_CLASSES.confirmCancel,
			title: m.event_group_timecodes_title(),
			text: m.event_group_timecodes_text(),
            icon: "question",
            showCancelButton: true,
			confirmButtonText: m.yes(),
			cancelButtonText: m.no(),
        });

        let captionFilePresent = captionFilePresentRes.isConfirmed;
		let syncSelected = false;
		if (eventGroupState[projectState.selected] && eventGroupState[projectState.selected].selected.length > 0){
			let syncSelectedEvents = await Swal.fire({
				...SWAL_BASE,
				customClass: SWAL_CLASSES.confirmCancel,
				title: "Sync Selected Events Only?",
				text: "Would you like to sync only the selected events? If you select no, all events in the event group will be synced.",
				icon: "question",
				showCancelButton: true,
				confirmButtonText: m.yes(),
				cancelButtonText: m.no(),
			});
		
			syncSelected = syncSelectedEvents.isConfirmed;
		}

		storageRef
			.child(storagePath + selectedJob.id + ".json")
			.getDownloadURL()
			.then((url) => {
				return fetch(url);
			})
			.then((res) => {
				return res.json();
			})
			.then(async (resJson) => {
				let srcProfile = selectedJob.config.provider;
				let options = new defaults.options({
					profile: formats.profileMapping[srcProfile].name,
					formatOptions: [
						{
							name: "Import Type",
							type: "list",
							values: ["subtitle", "transcription"],
							selected: "word map",
						},
						{
							name: "Max Characters",
							type: "number-input",
							values: "",
							selected: 32,
						},
						{
							name: "Max Lines",
							type: "number-input",
							values: "",
							selected: 3,
						},
					],
					frameRate: projectState.frameRate,
					dropFrame: projectState.dropFrame,
					window: {
                        width: styleState.width,
                        height: styleState.height,
                        xPadding: styleState.xPadding,
                        yPadding: styleState.yPadding
                    }
				});

				let wordMap = await decode(JSON.stringify(resJson), options);
				let eventGroup = JSON.parse(
					JSON.stringify(eventGroupState[projectState.selected]),
				);

				if (eventGroup.events.length === 0) {
					return;
				}

				//Calculate missing timecodes so that they don't get filtered out by the alignCaptionFile function.
				eventGroup.events.forEach((event, i, events) => {
					events[i].start = i + 0.25;
					events[i].end = i + 0.75;
				});

				let alignmentRes = await alignCaptionFile(
					eventGroup,
					wordMap,
					projectState.frameRate,
					captionFilePresent
				);

				if (syncSelected){
					// Only update the timing of the selected events
					eventGroupState[projectState.selected].selected.forEach((selectedEventIndex) => {
						let alignedEvent = alignmentRes.eventGroup.events[selectedEventIndex];
						if (alignedEvent){
							eventGroupState[projectState.selected].events[selectedEventIndex].start = alignedEvent.start;
							eventGroupState[projectState.selected].events[selectedEventIndex].end = alignedEvent.end;
						}
					});
				} else {
					eventGroupState[projectState.selected].events.forEach((ev, evIndex, evs) =>{
						let alignedEvent = alignmentRes.eventGroup.events[evIndex];
						if (alignedEvent){
							evs[evIndex].start = alignedEvent.start;
							evs[evIndex].end = alignedEvent.end;
						}
					})
				}
				

				if (alignmentRes.missingSegments.length > 0) {
					//Alert user of missing dialogue segments, and ask if they would like a new event group created with the missing dialogue.
					let swalRes = await Swal.fire({
						...SWAL_BASE,
						customClass: SWAL_CLASSES.confirmCancel,
						title: m.missing_dialogue_title(),
						text: m.missing_dialogue_text(),
						icon: "warning",
						showCancelButton: true,
						confirmButtonText: m.yes(),
						cancelButtonText: m.no(),
					});

					//If user selects yes, create a new event group with the missing dialogue segments and add it to the event group state.
					if (swalRes.isConfirmed) {
						let newEventGroup = {...(new _EventGroup({
								name: m.missing_dialogue_name({ index: eventGroupState.length }),
							type: "subtitle",
							maxCps: eventGroupDefaults.maxCps || 9999,
							maxWpm: eventGroupDefaults.maxWpm || 9999,
							maxChars: eventGroupDefaults.maxChars || 9999,
							maxLines: eventGroupDefaults.maxLines || 9999,
                            minDuration: eventGroupDefaults.minDuration || 0.5,
                            maxDuration: eventGroupDefaults.maxDuration || 12,
                            overlap: eventGroupDefaults.overlap,
							illegalChars: eventGroupDefaults.illegalChars,
						}))};

						alignmentRes.missingSegments.forEach((segment) => {
							newEventGroup.events.push(
								{...(new _Event({
									start: segment.start,
									end: segment.end,
									text: convertToHtml(segment.text),
								}))}
							);
						});

                        eventGroupState.push(newEventGroup);
					}
				}

				insertHistoryAction({
					name: "automatic sync", //action name
					eventGroup: projectState.selected,
					snapshots: [
						{
							store: "eventGroupState",
							value: JSON.stringify(eventGroupState),
						},
					],
				});

				toast.push(m.timing_applied_success(), {
					classes: ["toast-success"],
				});

				hideModal();
			})
			.catch((error) => {
				console.log(error, error.message);
				loading = false;

				toast.push(m.error_message({ error: error.message }), { classes: ["toast-danger"] });
			});
	}

	async function automaticSpeakerId() {
		disableAutoRefresh();

		if (loading) {
			return;
		}

		toast.push(m.identifying_speakers(), {
			classes: ["toast-info"],
		});

		loading = true;

		storageRef
			.child(storagePath + selectedJob.id + ".json")
			.getDownloadURL()
			.then((url) => {
				return fetch(url);
			})
			.then((res) => {
				return res.json();
			})
			.then(async (resJson) => {
				let srcProfile = selectedJob.config.provider;
				let options = new defaults.options({
					profile: formats.profileMapping[srcProfile].name,
					formatOptions: [
						{
							name: "Import Type",
							type: "list",
							values: ["subtitle", "transcription"],
							selected: "word map",
						},
						{
							name: "Max Characters",
							type: "number-input",
							values: "",
							selected: 32,
						},
						{
							name: "Max Lines",
							type: "number-input",
							values: "",
							selected: 3,
						},
					],
					frameRate: projectState.frameRate,
					dropFrame: projectState.dropFrame,
					window: {
                        width: styleState.width,
                        height: styleState.height,
                        xPadding: styleState.xPadding,
                        yPadding: styleState.yPadding
                    }
				});

				let wordMap = await decode(JSON.stringify(resJson), options);
				let eventGroup = JSON.parse(
					JSON.stringify(eventGroupState[projectState.selected]),
				);

				if (eventGroup.events.length === 0) {
					return;
				}

				//Calculate missing timecodes so that they don't get filtered out by the alignCaptionFile function.
				eventGroup.events.forEach((event, i, events) => {
					events[i].start = i + 0.25;
					events[i].end = i + 0.75;
				});

				let alignmentRes = await alignCaptionFile(
					eventGroup,
					wordMap,
					projectState.frameRate,
					false,
				);

				//alignmentRes.eventGroup.events now have a matches array with the words from the wordmap. The words have a speakers property array with the speaker name as the first index. We need to clear all speakers from the events in the selected event group. Then we need to assign a speaker to each event based on the speakers assigned to the words in the matches array. To do this we need to find the event with the same ID in the alignmentRes.eventGroup.events array and assign the speaker to the event in the eventGroupState array. We should check that all of the words the matches array have the same speaker assigned - if not, take the speaker that appears the most in the array. If there is a tie, take the first speaker in the array.

				//Clear all speakers from the events in the selected event group.
				eventGroupState[projectState.selected].events.forEach(
					(event, i, events) => {
						events[i].speakers = [];
					},
				);

				//Assign speakers to the events in the selected event group.
				eventGroupState[projectState.selected].events.forEach((event, index, events) =>{
					let eventMatch = alignmentRes.eventGroup.events.find((e) => {
						return e.id === event.id;
					});

					if (eventMatch && eventMatch.matches && eventMatch.matches.length > 0){
						let speakers = eventMatch.matches.map((m) => {
							return m.speakers[0];
						});

						let speakerCount = speakers.reduce((acc, speaker) => {
							acc[speaker] = (acc[speaker] || 0) + 1;
							return acc;
						}, {});

						let maxSpeaker = Object.keys(speakerCount).reduce((a, b) =>
							speakerCount[a] > speakerCount[b] ? a : b,
						);

						events[index].speakers = [maxSpeaker];
					}
				});

				//Call the update speaker store function to update the speaker store with the new speakers.
                speakerState.concat(eventGroupState[projectState.selected].speakers);
				eventGroupState[projectState.selected] = eventGroupState[projectState.selected];

				insertHistoryAction({
					name: "auto assign speakers", //action name
					eventGroup: projectState.selected,
					snapshots: [
                        {
							store: "speakerState",
							value: JSON.stringify(speakerState),
						},
						{
							store: "eventGroupState",
							value: JSON.stringify(eventGroupState),
						},
					],
				});

				toast.push(m.auto_assign_speakers_complete(), {
					classes: ["toast-success"],
				});

				hideModal();
			})
			.catch((error) => {
				console.log(error, error.message);
				loading = false;

				toast.push(m.error_message({ error: error.message }), { classes: ["toast-danger"] });
			});
	}

	async function createAdTemplate() {
		if (loading) {
			return;
		}

		const minGap = await promptAdTemplateMinGap(m);
		if (minGap === null) {
			return;
		}

		disableAutoRefresh();
		toast.push(m.creating_ad_template_wait(), {
			classes: ["toast-info"],
		});

		loading = true;

		let type = "transcription";

		storageRef
			.child(storagePath + selectedJob.id + ".json")
			.getDownloadURL()
			.then((url) => {
				return fetch(url);
			})
			.then((res) => {
				return res.json();
			})
			.then(async (resJson) => {
				let srcProfile = selectedJob.config.provider;
				let options = new defaults.options({
					profile: formats.profileMapping[srcProfile].name,
					formatOptions: [
						{
							name: "Import Type",
							type: "list",
							values: ["subtitle", "transcription"],
							selected: type,
						},
						{
							name: "Max Characters",
							type: "number-input",
							values: "",
							selected: maxChars,
						},
						{
							name: "Max Lines",
							type: "number-input",
							values: "",
							selected: maxLines,
						},
					],
					frameRate: projectState.frameRate,
					dropFrame: projectState.dropFrame,
					window: {
                        width: styleState.width,
                        height: styleState.height,
                        xPadding: styleState.xPadding,
                        yPadding: styleState.yPadding
                    }
				});

				const adEventGroupTemplate = buildAdEventGroupTemplate({
					name: m.ad_template_name({ index: eventGroupState.length }),
					eventGroupDefaults,
					adDefaults,
				});

				let transcriptEventGroup = await decode(
					JSON.stringify(resJson),
					options,
				);

                transcriptEventGroup = encodeHtml(transcriptEventGroup);
                transcriptEventGroup = fixOverlap(transcriptEventGroup, editorState.minFrameGap, projectState.frameRate);

				fillAdTemplateFromGaps(adEventGroupTemplate, {
					sourceEvents: transcriptEventGroup.events,
					projectDuration: selectedJob.duration,
					minGapSeconds: minGap,
				});

                eventGroupState.push(adEventGroupTemplate);
				projectState.selected = eventGroupState.length - 1;

				toast.push(m.ad_template_created_success(), {
					classes: ["toast-success"],
				});
				hideModal();
			})
			.catch((error) => {
				console.log(error, error.message);
				loading = false;

				toast.push(m.error_message({ error: error.message }), { classes: ["toast-danger"] });
			});
	}
	function archiveJob() {
		deletingFile = true;
		dbRef
			.doc(selectedJob.id)
			.update({
				archived: true,
			})
			.then(() => {
				toast.push(m.job_archived_success(), {
					classes: ["toast-success"],
				});
				gettingJobs = getTranscriptJobs();
			})
			.catch((e) => {
				toast.push(m.error_archiving_job({ error: e.message }), {
					classes: ["toast-danger"],
				});
				console.log(e, e.message);
			})
			.finally(() => {
				deletingFile = false;
			});
	}
	async function deleteTranscript() {
		deletingFile = true;

		//Confirm deletion using Swal
		let swalRes = await Swal.fire({
			...SWAL_BASE,
			customClass: SWAL_CLASSES.destructive,
			title: m.delete_transcript_title(),
			text: m.delete_transcript_text({ project: selectedJob.projectName }),
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: m.yes(),
			cancelButtonText: m.no(),
		});

		if (!swalRes.isConfirmed) {
			deletingFile = false;
			return;
		}

		firebase
			.functions()
			.httpsCallable("v8DeleteTranscript")(selectedJob)
			.then(() => {
				toast.push(m.transcript_file_deleted_success(), {
					classes: ["toast-success"],
				});
				gettingJobs = getTranscriptJobs();
			})
			.catch((e) => {
				toast.push(m.error_deleting_transcript_file({ error: e.message }), {
					classes: ["toast-danger"],
				});
				console.log(e, e.message);
			})
			.finally(() => {
				deletingFile = false;
			});
	}

	function exportCsv() {
		toast.push(m.exporting_results_wait(), {
			classes: ["toast-info"],
		});

		let csv = Papa.unparse(
			JSON.stringify(
				jobs.map((job) => {
					return {
						id: job.id,
						projectId: job.projectId,
						projectName: job.projectName,
						progress: job.progress,
						createdOn: job.createdOn.toDate(),
						completedOn: job.completedOn
							? job.completedOn.toDate()
							: "",
						updatedOn: job.updatedOn.toDate(),
						status: job.status,
						statusMsg: job.statusMsg,
						type: job.type,
						duration: job.duration,
						cost: job.cost,
						provider: job.config.provider || "",
						language: job.config.language,
					};
				}),
			),
		);

		let fileBlob = new Blob([csv], {
			type: "text/csv;charset=utf-8",
		});

		saveAs(fileBlob, "report_transcript_jobs.csv", {
			autoBom: true,
		});
	}
</script>

<div transition:fade={{ duration: 100 }} class="modal {modalState.selected === 'aiTranscriptImport' ? 'show d-block' : ''}" role="dialog" tabindex="-1" id="AiTranscriptImportModal" aria-labelledby="aiTranscriptImportTitle" aria-describedby="aiTranscriptImportDesc">
	<div class="modal-dialog modal-xl modal-dialog-centered" role="document">
		<div class="modal-content">
			<div class="modal-header">
				<h4 id="aiTranscriptImportTitle" class="modal-title">{m.ai_transcript_import()}</h4>
				<button type="button" class="btn-close" aria-label={m.close()} onclick={hideModal} disabled={batchProcessing} id="closeAiTranscriptImport"></button>
			</div>
			<div class="modal-body" id="aiTranscriptImportDesc">
				<div class="btn-group float-end mb-2" role="group">
					<button
						type="button"
						class="btn btn-outline-dark"
						onclick={exportCsv}
						disabled={jobs.length === 0}>{m.export_as_csv()}</button
					>
				</div>
				<!-- Search Form -->
				<form>
					<div class="row g-1">
						<div class="col-2">
							<select
								class="form-select me-2 mb-2"
								bind:value={dateRange}
							>
								<option value="today">{m.past_24_hours()}</option>
								<option value="week">{m.past_week()}</option>
								<option value="month">{m.past_month()}</option>
								<option value="year">{m.past_year()}</option>
								<option value="custom">{m.custom()}</option>
							</select>
						</div>
						{#if dateRange === "custom"}
							<div class="col-2">
								<input
									class="form-control me-2 mb-2"
									type="date"
									bind:value={startDate}
								/>
							</div>
							<div class="col-2">
								<input
									class="form-control me-2 mb-2"
									type="date"
									bind:value={endDate}
								/>
							</div>
						{/if}
						<div class="col-2">
							<select
								class="form-select me-2 mb-2"
								bind:value={statusFilter}
							>
								<option value="All">{m.all()}</option>
								<option value="Submitted">{m.submitted()}</option>
								<option value="In Progress">{m.in_progress()}</option>
								<option value="Passed">{m.passed()}</option>
								<option value="Failed">{m.failed()}</option>
							</select>
						</div>
						<div class="col">
							<div class="btn-group" role="group">
								<button
									type="button"
									class="btn btn-light"
									disabled={dateRange === "custom" &&
										(!startDate || !endDate)}
									onclick={() => {
										gettingJobs = getTranscriptJobs();
									}}>{m.refresh()}</button
								>
								<button
									type="button"
									class="btn {autoRefresh
										? 'btn-primary'
										: 'btn-light'}"
									onclick={() => {
										autoRefresh
											? disableAutoRefresh()
											: enableAutoRefresh();
									}}
									><i
										class="bi {autoRefresh
											? 'bi-check-circle-fill'
											: 'bi bi-circle'}"
									></i> {m.auto_refresh()}</button
								>
							</div>
						</div>
						{#if dateRange === "custom" && (!startDate || !endDate)}
							<p class="text-danger small pt-2 me-2">
									{m.custom_date_range_required()}
							</p>
						{/if}
						{#if loading}
							<div class="col">
								<Circle
									size="30"
									color="#1eb4b2"
									unit="px"
									duration="1s"
								></Circle>
							</div>
						{/if}
					</div>
				</form>
				<!-- Results Table -->
				<div
					id="TranscriptTableDashboard"
					class="table-responsive rounded shadow border border-secondary"
				>
					<table class="table table-hover table-sm">
						<thead class="bg-dark text-dark">
							<tr>
								<th class="text-truncate">{m.project_name()}</th>
								<th class="text-truncate">{m.remaining_time()}</th>
								<th class="text-truncate">{m.progress()}</th>
								<th>{m.status()}</th>
								<th class="text-truncate">{m.status_message()}</th>

								<th class="text-truncate">{m.media_duration()}</th>
								<th class="text-truncate">{m.job_duration()}</th>
								<th>{m.provider()}</th>
								<th>{m.language()}</th>
								<th class="text-truncate">{m.file_present()}</th>
								<th class="w-25">{m.job_id()}</th>
								<th class="text-truncate">{m.submitted_by()}</th>
								<th>{m.created_on()}</th>
								<th>{m.completed_on()}</th>
							</tr>
						</thead>
						<tbody>
							{#await gettingJobs}
								<tr>
									<td colspan="14" style="text-align: center; padding: 10%;">
										<BarLoader
											size="160"
											color="#1eb4b2"
											unit="px"
											duration="3s"
										></BarLoader>
									</td>
								</tr>
							{:then}
								{#each pagedJobs as job, index (job.id)}
									<tr
										class="job-row {selectedJobIds.includes(job.id)
											? 'table-warning'
											: ''}"
										onclick={(e) => {
											if (!batchProcessing) {
												handleRowClick(e, job, index);
											}
										}}
									>
										<td
											class="text-truncate"
											title={job.projectName}
											>{job.projectName}</td
										>

										<td
											class="text-truncate {job.duration &&
											job.status === 'In Progress'
												? 'text-primary'
												: ''}"
											>{job.duration &&
											job.status === "In Progress"
												? new Date(
														(job.duration / 2 +
															job.createdOn
																.seconds -
															Math.round(
																Date.now() /
																	1000,
															)) *
															1000,
													)
														.toISOString()
														.substr(11, 8)
												: "-"}</td>

										<td class="align-middle">
											<div
												class="progress"
												style="width:150px"
											>
												<div
													class="progress-bar {job.progress <
													100
														? 'progress-bar-animated'
														: ''} {job.status ===
													'Failed'
														? 'bg-danger'
														: ''} {job.status ===
													'Passed'
														? 'bg-primary'
														: ''} progress-bar-striped"
													role="progressbar"
													aria-valuemin="0"
													aria-valuemax="100"
													style="width: {job.duration &&
													job.status === 'In Progress'
														? Math.min(
																99,
																(
																	((Math.round(
																		Date.now() /
																			1000,
																	) -
																		job
																			.createdOn
																			.seconds) /
																		(job.duration /
																			2)) *
																	100
																).toFixed(0),
															)
														: job.progress}%"
												>
													{job.duration &&
													job.status === "In Progress"
														? Math.min(
																99,
																(
																	((Math.round(
																		Date.now() /
																			1000,
																	) -
																		job
																			.createdOn
																			.seconds) /
																		(job.duration /
																			2)) *
																	100
																).toFixed(0),
															)
														: job.progress}%
												</div>
											</div>
										</td>

										<td
											title={job.status}
											class="text-truncate {job.status ===
											'Passed'
												? 'text-primary'
												: ''} {job.status === 'Failed'
												? 'text-danger'
												: ''}">{job.status}</td
										>
										<td
											title={job.statusMsg}
											class="text-truncate"
											>{job.statusMsg}</td
										>

										<td
											class="text-truncate"
											title={job.duration
												? new Date(job.duration * 1000)
														.toISOString()
														.substr(11, 8)
												: ""}
											>{job.duration
												? new Date(job.duration * 1000)
														.toISOString()
														.substr(11, 8)
												: ""}</td
										>
										<td
											class="text-truncate"
											title={job.completedOn
												? new Date(
														(job.completedOn
															.seconds -
															job.createdOn
																.seconds) *
															1000,
													)
														.toISOString()
														.substr(11, 8)
												: ""}
											>{job.completedOn
												? new Date(
														(job.completedOn
															.seconds -
															job.createdOn
																.seconds) *
															1000,
													)
														.toISOString()
														.substr(11, 8)
												: ""}</td
										>
										<td
											class="text-truncate"
											title={job.config.provider}
											>{job.config.provider}</td
										>
										<td title={job.config.language}
											>{job.config.language}</td
										>

										<td title={job.statusMsg}
											><i
												class="bi {!job.deleted &&
												job.status === 'Passed'
													? 'bi-check-lg text-primary'
													: 'bi-x-lg text-light'}"
											></i></td
										>
										<td
											class="text-truncate small"
											title={job.id}>{job.id}</td
										>
										<td
											class="text-truncate"
											title={job.submittedBy || email}
											>{job.submittedBy || email}</td
										>
										<td
											class="text-truncate"
											title={job.createdOn.toDate()}
											>{job.createdOn
												.toDate()
												.toString()
												.substring(0, 24)}</td
										>
										<td
											class="text-truncate"
											title={job.completedOn
												? job.completedOn.toDate()
												: ""}
											>{job.completedOn
												? job.completedOn
														.toDate()
														.toString()
														.substring(0, 24)
												: ""}</td>
									</tr>
								{:else}
									<tr>
										<td colspan="14" class="text-light text-center m-5 text-truncate">
											{m.no_results_to_display()}
										</td>
									</tr>
								{/each}
							{/await}
						</tbody>
					</table>
				</div>
				<!-- Pagination Controls -->
				{#if jobs.length > 0}
					<div class="d-flex align-items-center justify-content-between mb-2">
						<div class="d-flex align-items-center gap-2">
							<small class="text-body-secondary">
								{m.page_x_of_y({ current: currentPage, total: totalPages })}
								&middot; {jobs.length}
							</small>
							<div class="btn-group" role="group">
								<button
									type="button"
									class="btn btn-sm btn-outline-secondary"
									disabled={batchProcessing || pagedJobs.length === 0}
									onclick={selectAllOnPage}>{m.select_all()}</button
								>
								<button
									type="button"
									class="btn btn-sm btn-outline-secondary"
									disabled={batchProcessing || selectedJobIds.length === 0}
									onclick={clearSelection}>{m.clear_selection()}</button
								>
							</div>
						</div>
						<div class="btn-group" role="group">
							<button
								type="button"
								class="btn btn-sm btn-light"
								disabled={currentPage <= 1 || batchProcessing}
								onclick={() => {
									lastSelectedIndex = null;
									if (currentPage > 1) currentPage--;
								}}>{m.previous()}</button
							>
							<button
								type="button"
								class="btn btn-sm btn-light"
								disabled={currentPage >= totalPages || batchProcessing}
								onclick={() => {
									lastSelectedIndex = null;
									if (currentPage < totalPages) currentPage++;
								}}>{m.next()}</button
							>
						</div>
					</div>
				{/if}
				<!-- Batch Progress -->
				{#if batchProcessing}
					<div class="mb-2">
						<small class="text-body-secondary">{batchStatusMsg}</small>
						<div class="progress" style="height: 22px;">
							<div
								class="progress-bar progress-bar-striped progress-bar-animated"
								role="progressbar"
								aria-valuenow={batchProgress}
								aria-valuemin="0"
								aria-valuemax="100"
								style="width: {batchProgress}%"
							>
								{batchProgress}%
							</div>
						</div>
					</div>
				{/if}
			</div>
			{#if selectedJobIds.length > 0}
			<div class="modal-footer">
				<!-- Batch Action Bar -->
				{#if selectedJobIds.length > 1}
					<div class="btn-group" role="group">
						<span class="btn btn-outline-secondary disabled">
							{m.jobs_selected({ count: selectedJobIds.length })}
						</span>
						<!-- Batch Download -->
						<div class="dropup">
							<button
								id="batchDownloadOptions"
								type="button"
								class="btn btn-outline-primary rounded-0 dropdown-toggle"
								onclick={() => {
									showBatchDownloadOptions = !showBatchDownloadOptions;
								}}
								disabled={batchProcessing || loading || deletingFile}
							>
								<i class="bi bi-download"></i> {m.batch_download()}
							</button>
							<ul
								class="dropdown-menu {showBatchDownloadOptions ? 'show' : ''}"
								aria-labelledby="batchDownloadOptions"
							>
								<li>
									<a class="dropdown-item" href="#!/" onclick={() => batchDownload("subRip")}>{m.subrip_srt()}</a>
								</li>
								<li>
									<a class="dropdown-item" href="#!/" onclick={() => batchDownload("webVtt")}>{m.webvtt_vtt()}</a>
								</li>
								<li>
									<a class="dropdown-item" href="#!/" onclick={() => batchDownload("lmsTranscript")}>{m.docx_docx()}</a>
								</li>
								<li>
									<a class="dropdown-item" href="#!/" onclick={() => batchDownload("textTranscript")}>{m.text_txt()}</a>
								</li>
								{#if authState.status !== "in_trial"}
									<li>
										<a class="dropdown-item" href="#!/" onclick={() => batchDownload("raw")}>{m.json_json()}</a>
									</li>
								{/if}
								{#if authState.plan === "pro" || authState.plan === "enterprise"}
									<li>
										<a class="dropdown-item" href="#!/" onclick={() => batchDownload("scenerist")}>{m.scenarist_scc()}</a>
									</li>
									<li>
										<a class="dropdown-item" href="#!/" onclick={() => batchDownload("macCaption")}>{m.maccaption_mcc()}</a>
									</li>
									<li>
										<a class="dropdown-item" href="#!/" onclick={() => batchDownload("ebuStl")}>{m.ebu_stl_stl()}</a>
									</li>
								{/if}
								<li><hr class="dropdown-divider" /></li>
								<li>
									<a class="dropdown-item" href="#!/" onclick={() => batchDownload("transcriptDocX")}>{m.docx_transcript()}</a>
								</li>
								<li>
									<a class="dropdown-item" href="#!/" onclick={() => batchDownload("transcriptTxt")}>{m.text_transcript()}</a>
								</li>
							</ul>
						</div>
						<!-- Batch Import -->
						<button
							type="button"
							class="btn btn-primary rounded-start-0"
							onclick={() => batchImport("subtitle")}
							disabled={batchProcessing || loading || deletingFile}
						>
							<i class="bi bi-pencil-square"></i> {m.batch_import()}
						</button>
					</div>
				{/if}
				{#if selectedJob}
					<div
						class="btn-group"
						role="group"
						aria-label={m.button_group_nested_dropdown()}
					>						
						<!-- Download -->
						<div class="dropup">
							<button
								id="downloadOptions"
								type="button"
								class="btn btn-outline-primary rounded-end-0 dropdown-toggle"
								onclick={() => {
									showDownloadOptions = !showDownloadOptions;
								}}
								disabled={loading ||
									deletingFile ||
									selectedJob.deleted ||
									selectedJob.status !== "Passed"}
							>
								<i class="bi bi-download"></i> {m.download()}
							</button>
							<ul
								class="dropdown-menu {showDownloadOptions
									? 'show'
									: ''}"
								aria-labelledby="moreOptions"
							>
								<li>
									<a
										class="dropdown-item"
										href="#!/"
										onclick={() =>
											downloadSubtitle("subRip")}
										>{m.subrip_srt()}</a
									>
								</li>
								<li>
									<a
										class="dropdown-item"
										href="#!/"
										onclick={() =>
											downloadSubtitle("webVtt")}
										>{m.webvtt_vtt()}</a
									>
								</li>
								<li>
									<a
										class="dropdown-item"
										href="#!/"
										onclick={() =>
											downloadSubtitle("lmsTranscript")}
										>{m.docx_docx()}</a
									>
								</li>
								<li>
									<a
										class="dropdown-item"
										href="#!/"
										onclick={() =>
											downloadSubtitle("textTranscript")}
										>{m.text_txt()}</a
									>
								</li>
								{#if authState.status !== "in_trial"}
									<li>
										<a
											class="dropdown-item"
											href="#!/"
											onclick={() =>
												downloadSubtitle("raw")}
											>{m.json_json()}</a
										>
									</li>
								{/if}
								{#if authState.plan === "pro" || authState.plan === "enterprise"}
									<li>
										<a
											class="dropdown-item"
											href="#!/"
											onclick={() =>
												downloadSubtitle("scenerist")}
											>{m.scenarist_scc()}</a
										>
									</li>
									<li>
										<a
											class="dropdown-item"
											href="#!/"
											onclick={() =>
												downloadSubtitle("macCaption")}
											>{m.maccaption_mcc()}</a
										>
									</li>
									<li>
										<a
											class="dropdown-item"
											href="#!/"
											onclick={() =>
												downloadSubtitle("ebuStl")}
											>{m.ebu_stl_stl()}</a
										>
									</li>
								{/if}
								<li><hr class="dropdown-divider" /></li>
								<li>
									<a
										class="dropdown-item"
										href="#!/"
										onclick={() =>
											downloadSubtitle("transcriptDocX")}
										>{m.docx_transcript()}</a
									>
								</li>
								<li>
									<a
										class="dropdown-item"
										href="#!/"
										onclick={() =>
											downloadSubtitle("transcriptTxt")}
										>{m.text_transcript()}</a
									>
								</li>
								<li><hr class="dropdown-divider" /></li>
								<li>
									<a
										class="dropdown-item"
										href="#!/"
										onclick={() => downloadSubtitle(false)}
										>{m.more_ellipsis()}</a
									>
								</li>
							</ul>
						</div>
						<!-- Edit Subtitles -->
						<button
							type="button"
							class="btn btn-primary rounded-start-0"
							onclick={() => importTranscript("subtitle")}
							disabled={loading ||
								deletingFile ||
								selectedJob.deleted ||
								selectedJob.status !== "Passed"}
						>
							<i class="bi bi-pencil-square"></i> {m.edit_subtitles()}
						</button>
					</div>
					<div class="btn-group" role="group">
						<!-- More Options -->
						<div class="dropup ms-2">
							<button
								id="moreOptions"
								type="button"
								class="btn btn-secondary text-white dropdown-toggle"
								onclick={() => {
									showOptions = !showOptions;
								}}
								disabled={loading ||
									deletingFile ||
									selectedJob.deleted ||
									selectedJob.status !== "Passed"}
							>
								{m.more_options()}
							</button>
							<ul
								class="dropdown-menu {showOptions
									? 'show'
									: ''}"
								aria-labelledby="moreOptions"
							>
								{#if authState.ad}
									<li>
										<a
											class="dropdown-item"
											href="#!/"
											onclick={createAdTemplate}
											>{m.create_ad_template()}</a
										>
									</li>
								{/if}
								{#if authState.plan === "pro" || authState.plan === "enterprise"}
									<li>
										<a
											class="dropdown-item"
											href="#!/"
											onclick={automaticSync}
											>{m.automatic_sync()}</a
										>
									</li>
									<li>
										<a
											class="dropdown-item"
											href="#!/"
											onclick={automaticSpeakerId}
											>{m.automatic_speaker_id()}</a>
									</li>
								{/if}
								<li><hr class="dropdown-divider"></li>
								{#if selectedJob.status !== "Passed"}
									<li>
										<a
											title={m.archive_job()}
											class="dropdown-item"
											href="#!/"
											disabled={deletingFile}
											onclick={archiveJob}
											>{m.archive_job()}</a
										>
									</li>
								{/if}
								{#if !selectedJob.deleted && selectedJob.status === "Passed"}
									<li>
										<a
											title={m.delete_transcript_title()}
											class="dropdown-item text-danger"
											href="#!/"
											disabled={deletingFile}
											onclick={deleteTranscript}
											><i class="bi bi-trash"></i> {m.transcript()}</a
										>
									</li>
								{/if}
							</ul>
						</div>
						<!-- More Options End -->
					</div>
				{/if}
			</div>
			{/if}
		</div>
	</div>
</div>

<style lang="css">
	#TranscriptTableDashboard {
		max-height: 500px;
		margin-bottom: 2%;
	}

	.job-row {
		cursor: pointer;
		user-select: none;
	}
</style>
